/**
 * Profile Service — Firestore CRUD for the operator's user document (web).
 *
 * Mirrors desktop/src/renderer/src/profile-service.ts.
 * Schema covers identity, business profile, operator config + guardrails,
 * connected provider metadata, onboarding progress, preferences,
 * and a lightweight activity summary.
 */

import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore'
import { db } from './firebase.js'

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

function defaultOperatorConfig() {
  return {
    approvalMode: 'manual_review',
    defaultEnvironment: 'personal',
    guardrails: {
      maxBudgetIncreasePercent: 20,
      maxSpendDeltaPerAction: 500,
      requireApprovalAboveAmount: 100
    }
  }
}

function defaultOnboarding() {
  return {
    startedAt: null,
    completedAt: null,
    steps: {
      business_profile: false,
      meta_connected: false,
      google_connected: false,
      engine_verified: false
    }
  }
}

function defaultPreferences() {
  return {
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    displayCurrency: 'USD',
    notifications: { email: true, desktop: true },
    morningBriefEnabled: false,
    watchLoopsEnabled: false
  }
}

function defaultActivitySummary() {
  return {
    totalActionsApproved: 0,
    totalActionsExecuted: 0,
    lastActionAt: null,
    platformsActive: []
  }
}

// ---------------------------------------------------------------------------
// Upsert on login
// ---------------------------------------------------------------------------

/**
 * Called immediately after every successful auth event.
 * - First login: seeds the full document with safe defaults.
 * - Return visit: updates identity fields and lastLoginAt only.
 * @param {import('firebase/auth').User} user
 */
export async function upsertUserOnLogin(user) {
  const ref = doc(db, 'users', user.uid)
  const snap = await getDoc(ref)

  const identity = {
    uid: user.uid,
    email: user.email ?? '',
    displayName: user.displayName ?? '',
    photoURL: user.photoURL ?? '',
    provider: user.providerData[0]?.providerId ?? 'password',
    emailVerified: user.emailVerified,
    lastLoginAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  }

  if (!snap.exists()) {
    await setDoc(ref, {
      ...identity,
      createdAt: serverTimestamp(),
      businessProfile: null,
      operatorConfig: defaultOperatorConfig(),
      connectedProviders: {},
      onboarding: defaultOnboarding(),
      preferences: defaultPreferences(),
      activitySummary: defaultActivitySummary()
    })
  } else {
    await setDoc(ref, identity, { merge: true })
  }
}

// ---------------------------------------------------------------------------
// CRUD
// ---------------------------------------------------------------------------

/**
 * @param {string} uid
 * @returns {Promise<object|null>}
 */
export async function getProfile(uid) {
  try {
    const snap = await getDoc(doc(db, 'users', uid))
    return snap.exists() ? snap.data() : null
  } catch {
    return null
  }
}

/**
 * @param {string} uid
 * @param {object} data
 */
export async function saveProfile(uid, data) {
  await setDoc(
    doc(db, 'users', uid),
    { ...data, uid, updatedAt: serverTimestamp() },
    { merge: true }
  )
}

/**
 * @param {string} uid
 * @param {object} bp
 */
export async function saveBusinessProfile(uid, bp) {
  await setDoc(
    doc(db, 'users', uid),
    {
      uid,
      businessProfile: { ...bp, updatedAt: serverTimestamp() },
      updatedAt: serverTimestamp()
    },
    { merge: true }
  )
}

/**
 * @param {string} uid
 * @param {object} config
 */
export async function saveOperatorConfig(uid, config) {
  await setDoc(
    doc(db, 'users', uid),
    { uid, operatorConfig: config, updatedAt: serverTimestamp() },
    { merge: true }
  )
}

/**
 * @param {string} uid
 * @param {object} prefs
 */
export async function savePreferences(uid, prefs) {
  await setDoc(
    doc(db, 'users', uid),
    { uid, preferences: prefs, updatedAt: serverTimestamp() },
    { merge: true }
  )
}

/**
 * Mark an onboarding step as complete.
 * @param {string} uid
 * @param {'business_profile'|'meta_connected'|'google_connected'|'engine_verified'} step
 */
export async function markOnboardingStep(uid, step) {
  const profile = await getProfile(uid)
  const current = profile?.onboarding ?? defaultOnboarding()
  const steps = { ...current.steps, [step]: true }
  const allDone = Object.values(steps).every(Boolean)

  await setDoc(
    doc(db, 'users', uid),
    {
      uid,
      onboarding: {
        ...current,
        startedAt: current.startedAt ?? serverTimestamp(),
        steps,
        completedAt: allDone ? serverTimestamp() : (current.completedAt ?? null)
      },
      updatedAt: serverTimestamp()
    },
    { merge: true }
  )
}
